import React, { useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { FiMessageCircle, FiSend, FiX } from "react-icons/fi";
import { useAuth } from "context/auth-context";
import { toastrError, toastrSuccess } from "../../lib/toastr";
import { submitFeedback } from "./FeedbackWidget.api";

type Category = "Feedback" | "Bug Report" | "Support Request" | "Other";

type FormState = {
  feedbackType: Category;
  name: string;
  email: string;
  subject: string;
  message: string;
};

type Errors = {
  name: string;
  email: string;
  message: string;
};

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const FeedbackWidget = () => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [isOpen, setIsOpen] = useState(false);

  const initialState = useMemo<FormState>(
    () => ({
      feedbackType: "Feedback",
      name: auth?.name || "",
      email: auth?.email || "",
      subject: "",
      message: "",
    }),
    [auth]
  );

  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Errors>({ email: "", message: "", name: "" });

  const categories: Array<{ value: Category; label: string }> = [
    { value: "Feedback", label: t("feedback.category.feedback") },
    { value: "Bug Report", label: t("feedback.category.bug") },
    { value: "Support Request", label: t("feedback.category.support") },
    { value: "Other", label: t("feedback.category.other") },
  ];

  const openModal = () => {
    setForm(initialState);
    setErrors({ email: "", message: "", name: "" });
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const mutation = useMutation(submitFeedback, {
    onSuccess: (response: any) => {
      if (response?.success) {
        toastrSuccess(t("feedback.successMsg"));
        setIsOpen(false);
      } else {
        toastrError(response?.message || t("feedback.errorMsg"));
      }
    },
    onError: (err: any) => {
      toastrError(err?.response?.data?.message || t("feedback.errorMsg"));
    },
  });

  const validate = (values: FormState): Errors => {
    const nextErrors: Errors = {
      name: "",
      email: "",
      message: "",
    };

    // Name
    if (!values.name.trim()) {
      nextErrors.name = t("feedback.validation.name");
    }

    // Email
    if (!values.email.trim()) {
      nextErrors.email = t("feedback.validation.email");
    } else if (!EMAIL_REGEX.test(values.email.trim())) {
      nextErrors.email = t("feedback.validation.email");
    }

    // Message
    if (!values.message.trim()) {
      nextErrors.message = t("feedback.validation.message");
    }

    return nextErrors;
  };

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => {
      const updated = { ...prev };

      switch (name) {
        case "name":
          updated.name = value.trim()
            ? ""
            : t("feedback.validation.name");
          break;

        case "email":
          if (!value.trim()) {
            updated.email = t("feedback.validation.email");
          } else if (!EMAIL_REGEX.test(value.trim())) {
            updated.email = t("feedback.validation.email");
          } else {
            updated.email = "";
          }
          break;

        case "message":
          updated.message = value.trim()
            ? ""
            : t("feedback.validation.message");
          break;

        default:
          break;
      }

      return updated;
    });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    const nextErrors = validate(form);

    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }

    const currentPage = `${location.pathname}${location.search || ""}`;

    mutation.mutate({
      feedbackType: form.feedbackType,
      subject: form.subject.trim(),
      message: form.message.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      userId: auth?.id ?? null,
      panelistId: auth?.panelistId ?? searchParams.get("UID") ?? null,
      sessionId: searchParams.get("SESSION_ID"),
      studyId: searchParams.get("SID"),
      currentPage,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    });
  };

  // Signed-in mobile screens have a bottom nav (64px + safe area); the trigger floats above it.
  // The cookie-settings link sits bottom-left, so the trigger stays on the right.
  const triggerOffset = auth
    ? "bottom-[calc(var(--zs-bottomnav-h)_+_env(safe-area-inset-bottom,0px)_+_16px)] lg:bottom-6"
    : "bottom-[calc(env(safe-area-inset-bottom,0px)_+_16px)] lg:bottom-6";

  return (
    <>
      <button
        type="button"
        aria-label={t("feedback.title")}
        title={t("feedback.title")}
        onClick={openModal}
        className={`fixed z-50 right-4 lg:right-6 ${triggerOffset} inline-flex items-center justify-center w-12 h-12 rounded-full cursor-pointer bg-primary text-ink-inverse shadow-lg ring-4 ring-primary/10 transition hover:bg-primary-hover hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-primary-ring`}
      >
        <FiMessageCircle className="w-[22px] h-[22px]" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="zs-backdrop items-end sm:items-center" onClick={closeModal}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="fb_title"
            className="zs-modal max-w-[520px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start gap-3 px-5 pt-5 pb-4 border-b sm:px-6 border-line">
              <span className="zs-stat-icon">
                <FiMessageCircle aria-hidden="true" />
              </span>
              <div className="flex-1 min-w-0">
                <h2 id="fb_title" className="text-base font-bold font-display text-ink">
                  {t("feedback.title")}
                </h2>
                <p className="mt-0.5 text-sm text-ink-3">{t("feedback.subtitle")}</p>
              </div>
              <button
                type="button"
                aria-label={t("feedback.close")}
                onClick={closeModal}
                className="-mt-1 -mr-2 zs-icon-btn shrink-0"
              >
                <FiX />
              </button>
            </div>

            <form className="flex flex-col gap-4 px-5 py-5 sm:px-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="feedbackType" className="zs-label">
                  {t("feedback.field.category")}
                </label>
                <select
                  id="feedbackType"
                  name="feedbackType"
                  value={form.feedbackType}
                  onChange={handleChange}
                  className="zs-select"
                >
                  {categories.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="min-w-0">
                  <label htmlFor="fb_name" className="zs-label zs-label-required">
                    {t("feedback.field.name")}
                  </label>
                  <input
                    id="fb_name"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    maxLength={128}
                    placeholder={t("feedback.placeholder.name")}
                    aria-invalid={errors.name ? true : undefined}
                    className="zs-input"
                  />

                  {errors.name && <p className="zs-error">{errors.name}</p>}
                </div>
                <div className="min-w-0">
                  <label htmlFor="fb_email" className="zs-label zs-label-required">
                    {t("feedback.field.email")}
                  </label>
                  <input
                    id="fb_email"
                    type="text"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    maxLength={191}
                    placeholder={t("feedback.placeholder.email")}
                    aria-invalid={errors.email ? true : undefined}
                    className="zs-input"
                  />
                  {errors.email ? <p className="zs-error">{errors.email}</p> : null}
                </div>
              </div>

              <div>
                <label htmlFor="fb_subject" className="zs-label">
                  {t("feedback.field.subject")}
                </label>
                <input
                  id="fb_subject"
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  maxLength={255}
                  placeholder={t("feedback.placeholder.subject")}
                  className="zs-input"
                />
              </div>

              <div>
                <label htmlFor="fb_message" className="zs-label zs-label-required">
                  {t("feedback.field.message")}
                </label>
                <textarea
                  id="fb_message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  maxLength={5000}
                  rows={5}
                  placeholder={t("feedback.placeholder.message")}
                  aria-invalid={errors.message ? true : undefined}
                  className={`zs-textarea resize-none ${errors.message ? "border-danger" : ""}`}
                />
                {errors.message ? <p className="zs-error">{errors.message}</p> : null}
              </div>

              <div className="flex justify-end pt-4 -mx-5 px-5 border-t sm:-mx-6 sm:px-6 border-line">
                <button
                  type="submit"
                  disabled={mutation.isLoading}
                  className="w-full zs-btn zs-btn-primary sm:w-auto sm:min-w-[160px]"
                >
                  <FiSend aria-hidden="true" />
                  {mutation.isLoading ? t("feedback.submitting") : t("feedback.submit")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
